// Les étapes du parcours : à qui chaque questionnaire part, et quand.
//
// Le logiciel ne savait écrire qu'aux apprenants inscrits, et à deux moments
// écrits dans le planificateur. Les enquêtes formateur, entreprise et
// financeur étaient écrites mais ne pouvaient atteindre personne.
//
// **L'envoi automatique arrive à l'arrêt** sur les étapes qui n'ont jamais
// rien envoyé : les allumer toutes d'un coup arroserait les formations des
// deux derniers mois. C'est cet écran qui les met en route, une fois qu'on a
// regardé ce qu'elles enverraient.
//
// Deux avertissements, et ils comptent autant que les réglages : un envoi
// automatique sans questionnaire ne part nulle part, et deux questionnaires
// actifs pour la même étape font que le logiciel en choisit un **sans le
// dire** — l'autre ne partira jamais.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Send, Hand, BellRing } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';
import { messageErreur } from '../../lib/erreur';

export default function TypesQuestionnaireSection() {
  const [data, setData] = useState(null);
  const [occupe, setOccupe] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data: d } = await api.get('/types-questionnaire');
      setData(d);
    } catch (err) {
      toast({ title: 'Chargement impossible', description: messageErreur(err),
              variant: 'destructive' });
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const modifier = async (code, champ, valeur) => {
    setOccupe(code);
    try {
      await api.put(`/types-questionnaire/${code}`, { [champ]: valeur });
      await charger();
    } catch (err) {
      toast({ title: 'Modification refusée', description: messageErreur(err),
              variant: 'destructive' });
    } finally { setOccupe(null); }
  };

  if (!data) {
    return (
      <div className="soft-card p-5 text-[13px] text-[#8E8FA5]">Chargement…</div>
    );
  }

  const aucunAuto = data.types.filter(
    (t) => t.envoi_auto && !t.questionnaires_actifs).length;
  const enConflit = data.types.filter((t) => t.en_conflit).length;

  return (
    <div className="soft-card p-5" data-testid="section-types-questionnaire">
      <div className="text-[15px] font-semibold text-[#1A1B2E]"
           style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Les étapes du parcours
      </div>
      <div className="text-[12.5px] text-[#6B6C85] mt-1 mb-4">
        Chaque étape dit à qui son questionnaire part et quand. L'envoi à la
        main depuis une formation reste possible pour toutes, quel que soit
        le moment.
      </div>

      {(aucunAuto > 0 || enConflit > 0) && (
        <div className="rounded-lg border border-[#FCD9A6] bg-[#FEF3E7] p-3 mb-4"
             data-testid="types-avertissement">
          <div className="text-[12px] font-bold text-[#B45309] flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> À regarder
          </div>
          <ul className="mt-1 space-y-0.5 text-[11.5px] text-[#B45309]/90">
            {aucunAuto > 0 && (
              <li>
                • {aucunAuto} étape{aucunAuto > 1 ? 's sont' : ' est'} en envoi
                automatique sans aucun questionnaire : rien ne partira.
              </li>
            )}
            {data.types.filter((t) => t.en_conflit).map((t) => (
              <li key={t.code}>
                • <strong>{t.libelle}</strong> — {(t.conflits || [])[0]?.phrase}
                {' '}Rattachez-les à leur formation, ou éteignez celui que vous
                n&rsquo;utilisez pas.
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6]">
              <th className="text-left py-2 pr-3">Étape</th>
              <th className="text-left py-2 pr-3">Destinataire</th>
              <th className="text-left py-2 pr-3">Moment</th>
              <th className="text-left py-2 pr-3">Questionnaires</th>
              <th className="text-left py-2 pr-3">Envoi automatique</th>
              <th className="text-left py-2">Relances</th>
            </tr>
          </thead>
          <tbody>
            {data.types.map((t) => {
              const libre = t.moment === 'libre';
              return (
                <tr key={t.code} data-testid={`type-${t.code}`}
                    className="border-b border-[#F6F7FB] last:border-0">
                  <td className="py-2.5 pr-3">
                    <div className="font-semibold text-[#1A1B2E]">{t.libelle}</div>
                    <div className="text-[10.5px] text-[#B8B9CC]">{t.code}</div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <select
                      value={t.destinataire} disabled={occupe === t.code}
                      data-testid={`destinataire-${t.code}`}
                      onChange={(e) => modifier(t.code, 'destinataire', e.target.value)}
                      className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]"
                    >
                      {data.destinataires.map((d) => (
                        <option key={d.code} value={d.code}>{d.libelle}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2.5 pr-3">
                    <select
                      value={t.moment} disabled={occupe === t.code}
                      data-testid={`moment-${t.code}`}
                      onChange={(e) => modifier(t.code, 'moment', e.target.value)}
                      className="h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12px]"
                    >
                      {data.moments.map((m) => (
                        <option key={m.code} value={m.code}>{m.libelle}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2.5 pr-3">
                    {t.questionnaires_actifs === 0 ? (
                      <span className="text-[11.5px] text-[#B45309] font-semibold">
                        aucun
                      </span>
                    ) : (
                      <span className={`text-[11.5px] font-semibold ${
                        t.en_conflit ? 'text-[#B45309]' : 'text-[#0F766E]'
                      }`}
                            title={t.rattaches
                              ? `${t.rattaches} rattaché(s) à une formation, ${t.generiques} pour les autres`
                              : undefined}>
                        {t.rangement || t.questionnaires_actifs}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3">
                    {libre ? (
                      <span className="inline-flex items-center gap-1 text-[11.5px] text-[#8E8FA5]"
                            title="Sans moment, une étape ne part qu'à la demande">
                        <Hand className="w-3.5 h-3.5" /> à la demande
                      </span>
                    ) : (
                      <button
                        onClick={() => modifier(t.code, 'envoi_auto', !t.envoi_auto)}
                        disabled={occupe === t.code}
                        data-testid={`auto-${t.code}`}
                        title={t.envoi_auto
                          ? 'Part tout seul — cliquer pour arrêter'
                          : 'À l\'arrêt — cliquer pour mettre en route'}
                        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[11.5px] font-semibold border transition-colors ${
                          t.envoi_auto
                            ? 'bg-[#E6FBF7] text-[#0F766E] border-[#A7F3E4]'
                            : 'bg-white text-[#6B6C85] border-[#EFF0F6] hover:bg-[#F6F7FB]'
                        }`}
                      >
                        {occupe === t.code
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Send className="w-3.5 h-3.5" />}
                        {t.envoi_auto ? 'En marche' : 'À l\'arrêt'}
                      </button>
                    )}
                  </td>
                  <td className="py-2.5">
                    {/* Deux relances, J+3 et J+7, puis plus rien. Elles arrivent
                        à l'arrêt même sur une étape qui envoie déjà : des
                        invitations sans réponse dorment en base, et personne
                        n'a demandé qu'on les relance ce soir. */}
                    <button
                      onClick={() => modifier(t.code, 'relances_auto', !t.relances_auto)}
                      disabled={occupe === t.code}
                      data-testid={`relances-${t.code}`}
                      title={t.relances_auto
                        ? 'Relance à J+3 et J+7 — cliquer pour arrêter'
                        : 'Aucune relance — cliquer pour mettre en route'}
                      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[11.5px] font-semibold border transition-colors ${
                        t.relances_auto
                          ? 'bg-[#FEF3E7] text-[#B45309] border-[#FCD9A6]'
                          : 'bg-white text-[#6B6C85] border-[#EFF0F6] hover:bg-[#F6F7FB]'
                      }`}
                    >
                      {occupe === t.code
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <BellRing className="w-3.5 h-3.5" />}
                      {t.relances_auto ? 'J+3 et J+7' : 'Aucune'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[11.5px] text-[#8E8FA5] mt-4 leading-relaxed">
        Un questionnaire se rattache à une étape dans le constructeur de
        questionnaires. Une étape sans moment ne part qu'à la demande, depuis
        la formation. Les relances s'arrêtent après la seconde : une relance
        qui revient indéfiniment finit en courrier indésirable.
      </div>
    </div>
  );
}
